'use client';

import { c as _c } from "react/compiler-runtime";
import { type PinnedFactiii } from '@shared/all/domains/factiiis';
import { getDimensionsFromUri } from '@shared/utils/helpers/uploadHelper';
import useSpace from '@shared/utils/hooks/useSpace';
import useUpload from '@shared/utils/hooks/useUpload';
import { Camera } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { getS3ObjectURL } from '../../utils/s3';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Upload from '../common/Upload';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
enum UploadType {
  BANNER = 'Banner',
  AVATAR = 'Avatar',
}
type Props = {
  closeHandler: () => void;
  pinnedFactiiis: PinnedFactiii[];
  space: RouterOutputs['spaces']['get'];
};
type SpaceData = {
  description: string | null;
};
const SpaceEdit = props => {
  const $ = _c(65);
  const {
    updateSpace
  } = useSpace();
  const {
    upload,
    addFile,
    files,
    clear
  } = useUpload(client);
  const t0 = props.space.description ?? null;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      description: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialData = t1;
  const avatarKey = files.avatar?.uri ?? props.space.avatarKey ?? null;
  const bannerKey = files.banner?.uri ?? props.space.bannerKey ?? null;
  const [spaceData, setSpaceData] = useState(initialData);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(null);
  const hasChanges = spaceData.description !== initialData.description || !!files.avatar || !!files.banner;
  let t2;
  if ($[2] !== files.avatar || $[3] !== files.banner || $[4] !== props || $[5] !== spaceData.description || $[6] !== updateSpace || $[7] !== upload) {
    t2 = async () => {
      setButtonLoading(true);
      const avatarChanged = !!files.avatar;
      const bannerChanged = !!files.banner;
      const results = await upload();
      if ((avatarChanged || bannerChanged) && Object.keys(results).length === 0) {
        setButtonLoading(false);
        return;
      }
      const avatarId = results.avatar?.id;
      const bannerId = results.banner?.id;
      const [error, updatedSpace] = await client.spaces.update.mutate({
        slug: props.space.spaceSlug,
        description: spaceData.description ?? undefined,
        bannerId: bannerChanged ? bannerId : undefined,
        avatarId: avatarChanged ? avatarId : undefined
      });
      if (error) {
        return setButtonLoading(false);
      }
      updateSpace({
        ...props.space,
        ...updatedSpace
      });
      props.closeHandler();
      setButtonLoading(false);
    };
    $[2] = files.avatar;
    $[3] = files.banner;
    $[4] = props;
    $[5] = spaceData.description;
    $[6] = updateSpace;
    $[7] = upload;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const saveHandler = t2;
  let t3;
  if ($[9] !== addFile) {
    t3 = type => async file => {
      const key = type === UploadType.AVATAR ? "avatar" : "banner";
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      addFile(key, {
        uri,
        fileName: file.name,
        width: dimensions?.width,
        height: dimensions?.height
      });
      setUploadModalOpen(null);
    };
    $[9] = addFile;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const uploadCallbackFunction = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setUploadModalOpen(UploadType.BANNER);
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Button text="Edit Cover Photo" variant="CLEAR" classNames="absolute top-2 w-auto right-2" onClick={t4} titleElement={<div className="flex items-center justify-around space-x-2 text-sm font-semibold"><Camera height={15} width={15} /><p>Edit Cover Photo</p></div>} />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      width: "calc(100% + 1rem)",
      height: "auto"
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== bannerKey) {
    t7 = bannerKey ? getS3ObjectURL(bannerKey) : "/images/header-image-default.jpg";
    $[14] = bannerKey;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== bannerKey) {
    t8 = bannerKey?.startsWith("blob:");
    $[16] = bannerKey;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = !!t8;
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <div className="relative">{t5}<Image height={0} width={0} sizes="100vw" style={t6} src={t7} unoptimized={t9} alt="space banner" className="rounded-lg" /></div>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== avatarKey) {
    t11 = <Avatar size={100} spaceAvatarKey={avatarKey} className="-mt-14 ml-4 border-4 border-background-secondary" />;
    $[21] = avatarKey;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="absolute bottom-2 right-0"><Button variant="CLEAR" onClick={() => setUploadModalOpen(UploadType.AVATAR)} icon={<Camera height={15} width={15} />} tooltip="Edit Space Avatar" /></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11) {
    t13 = <div className="flex"><div className="relative">{t11}{t12}</div></div>;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t13) {
    t14 = <>{t10}{t13}</>;
    $[26] = t10;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const BannerComponent = t14;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <h2>Edit Space</h2>;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== props.closeHandler) {
    t16 = <ModalHeader closeHandler={props.closeHandler}>{t15}</ModalHeader>;
    $[30] = props.closeHandler;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = spaceData.description ?? "";
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = value => setSpaceData(prev => ({
      ...prev,
      description: value || null
    }));
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17) {
    t19 = <Input type="textarea" label="Description" value={t17} onChange={t18} />;
    $[33] = t17;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== BannerComponent || $[36] !== t19) {
    t20 = <ModalContent banner={BannerComponent}>{t19}</ModalContent>;
    $[35] = BannerComponent;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== props.closeHandler) {
    t21 = <Button text="Close" variant="SECONDARY" onClick={props.closeHandler} />;
    $[38] = props.closeHandler;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== clear || $[41] !== hasChanges || $[42] !== initialData) {
    t22 = hasChanges && <Button text="Cancel" variant="SECONDARY" onClick={() => {
      setSpaceData(initialData);
      clear();
    }} />;
    $[40] = clear;
    $[41] = hasChanges;
    $[42] = initialData;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = buttonLoading || !hasChanges;
  let t24;
  if ($[44] !== buttonLoading || $[45] !== saveHandler || $[46] !== t23) {
    t24 = <Button text="Save" onClick={saveHandler} loading={buttonLoading} disabled={t23} />;
    $[44] = buttonLoading;
    $[45] = saveHandler;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t22 || $[49] !== t24) {
    t25 = <div className="flex gap-2">{t22}{t24}</div>;
    $[48] = t22;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t21 || $[52] !== t25) {
    t26 = <ModalFooter><div className="flex w-full items-center justify-between">{t21}{t25}</div></ModalFooter>;
    $[51] = t21;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== props.closeHandler || $[55] !== t16 || $[56] !== t20 || $[57] !== t26) {
    t27 = <Modal closeHandler={props.closeHandler}>{t16}{t20}{t26}</Modal>;
    $[54] = props.closeHandler;
    $[55] = t16;
    $[56] = t20;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== uploadCallbackFunction || $[60] !== uploadModalOpen) {
    t28 = uploadModalOpen !== null && <Upload crop={{
      enabled: true,
      aspect: uploadModalOpen === UploadType.BANNER ? 3 : 1
    }} title={uploadModalOpen === UploadType.AVATAR ? "Upload Space Avatar" : "Upload Space Cover Photo"} closeHandler={() => setUploadModalOpen(null)} callbackFn={uploadCallbackFunction(uploadModalOpen)} enableCamera={true} />;
    $[59] = uploadCallbackFunction;
    $[60] = uploadModalOpen;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t27 || $[63] !== t28) {
    t29 = <>{t27}{t28}</>;
    $[62] = t27;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  return t29;
};
export default SpaceEdit;